/**
 * ExplainabilityModal Component
 * Phase 3.6 - Shows why AI made specific interpretations
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { HelpCircle, X, CheckCircle, AlertTriangle } from 'lucide-react';

const ExplainabilityModal = ({ section, lang = 'tr', onClose }) => {
  const modalRef = useRef(null);
  
  // Focus trap
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);
  
  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  
  if (!section) return null;
  
  return (
    <motion.div
      className="explainability-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="explainability-modal-overlay"
    >
      <motion.div
        className="explainability-modal"
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="explainability-title"
        aria-describedby="explainability-content"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="explainability-modal"
      >
        <div className="explainability-modal-header">
          <h3 id="explainability-title">
            <HelpCircle size={20} />
            {lang === 'tr' ? 'Bu yorum neden böyle?' : 'Why this interpretation?'}
          </h3>
          <button
            className="explainability-close"
            onClick={onClose}
            aria-label={lang === 'tr' ? 'Kapat' : 'Close'}
            data-testid="explainability-close"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="explainability-modal-content" id="explainability-content">
          {section.status === 'generated' && section.explainability && (
            <div className="explainability-text">
              <CheckCircle size={18} className="explainability-icon" />
              <p>{section.explainability}</p>
            </div>
          )}
          
          {section.status === 'limited' && (
            <div className="explainability-limited-text">
              <AlertTriangle size={18} className="explainability-icon limitation" />
              <p>
                {section.explainability || (lang === 'tr'
                  ? 'Mevcut gözlemler bu alan için yeterli güvenilir bilgi sağlayamadı.'
                  : 'Available observations could not provide sufficient reliable information for this area.'
                )}
              </p>
            </div>
          )}
          
          {section.source_features?.length > 0 && (
            <div className="explainability-features">
              <span className="features-label">
                {lang === 'tr' ? 'Kaynak gözlemler:' : 'Source observations:'}
              </span>
              <span className="features-count">
                {section.source_features.length} {lang === 'tr' ? 'özellik' : 'features'}
              </span>
            </div>
          )}
        </div>
        
        <div className="explainability-modal-footer">
          <button
            className="explainability-ok-btn"
            onClick={onClose}
            data-testid="explainability-ok"
          >
            {lang === 'tr' ? 'Anladım' : 'Got it'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

ExplainabilityModal.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    explainability: PropTypes.string,
    source_features: PropTypes.array,
    limitations: PropTypes.array
  }),
  lang: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

export default ExplainabilityModal;
